import { useTranslation } from 'react-i18next';
import { type DemoFarmInputs, type DemoFarmResults, type CropConfig, type AreaUnit, type WeightUnit, AREA_FACTORS, WEIGHT_FACTORS, AREA_LABELS, WEIGHT_LABELS, getCropBgColor } from './types';
import { NumberInput } from './NumberInput';
import { formatNumber, formatCurrency } from './utils';
import { CalculationBreakdown } from './CalculationBreakdown';
import { CurrencySelector } from './CurrencySelector';
import { UnitToggle } from './UnitToggle';

interface Props {
  inputs: DemoFarmInputs;
  onChange: (inputs: DemoFarmInputs) => void;
  results: DemoFarmResults;
  crops: CropConfig[];
  currencyCode: string;
  onCurrencyChange: (code: string) => void;
  exchangeRate: number;
  isLive: boolean;
  isLoading: boolean;
  areaUnit: AreaUnit;
  onAreaUnitChange: (unit: AreaUnit) => void;
  weightUnit: WeightUnit;
  onWeightUnitChange: (unit: WeightUnit) => void;
}

const AREA_OPTIONS: { value: AreaUnit; label: string }[] = [
  { value: 'ha', label: 'Hectares' },
  { value: 'acres', label: 'Acres' },
];

const WEIGHT_OPTIONS: { value: WeightUnit; label: string }[] = [
  { value: 'kg', label: 'Kg' },
  { value: 'lbs', label: 'lbs' },
  { value: 'g', label: 'g' },
];

export function DemoFarmSection({
  inputs, onChange, results, crops, currencyCode, onCurrencyChange,
  exchangeRate, isLive, isLoading,
  areaUnit, onAreaUnitChange, weightUnit, onWeightUnitChange,
}: Props) {
  const { t } = useTranslation();
  const totalPercentage = crops.reduce((sum, crop) => sum + (inputs.cropPercentages[crop.id] ?? 0), 0);
  const isValidPercentage = Math.abs(totalPercentage - 100) < 0.01;

  const af = AREA_FACTORS[areaUnit];
  const wf = WEIGHT_FACTORS[weightUnit];
  const aLabel = AREA_LABELS[areaUnit];
  const wLabel = WEIGHT_LABELS[weightUnit];

  const updateCropPercentage = (cropId: string, value: number) => {
    onChange({
      ...inputs,
      cropPercentages: { ...inputs.cropPercentages, [cropId]: value },
    });
  };

  const updateSeedRate = (cropId: string, displayValue: number) => {
    onChange({
      ...inputs,
      seedRatesKgPerHa: { ...inputs.seedRatesKgPerHa, [cropId]: displayValue * af / wf },
    });
  };

  const updateSeedCost = (cropId: string, displayValue: number) => {
    onChange({
      ...inputs,
      seedCostsLocalPerKg: { ...inputs.seedCostsLocalPerKg, [cropId]: displayValue * wf },
    });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      {/* Inputs Card */}
      <div className="card !p-4 md:!p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-spi-green to-spi-green-dark flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-display text-spi-green-dark leading-tight">{t('demoFarm.inputsTitle')}</h3>
            <p className="text-xs text-spi-slate">{t('demoFarm.inputsSubtitle')}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="input-label mb-0">{t('demoFarm.farmSize')}</label>
              <UnitToggle options={AREA_OPTIONS} value={areaUnit} onChange={onAreaUnitChange} />
            </div>
            <div className="relative">
              <input
                type="number"
                value={parseFloat((inputs.farmHectares * af).toFixed(4))}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val)) onChange({ ...inputs, farmHectares: val / af });
                }}
                min={0}
                step={1}
                className="input pr-20"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-spi-slate font-medium">
                {aLabel}
              </span>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="input-label mb-0">{t('demoFarm.cropAllocation')}</label>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                isValidPercentage
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {totalPercentage.toFixed(0)}% / 100%
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {crops.map((crop) => (
                <NumberInput
                  key={crop.id}
                  label={crop.label}
                  value={inputs.cropPercentages[crop.id] ?? 0}
                  onChange={(v) => updateCropPercentage(crop.id, v)}
                  suffix="%"
                  min={0}
                  max={100}
                  step={5}
                  compact
                  accentColor={crop.color}
                />
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="input-label mb-0">{t('demoFarm.seedRates')}</label>
              <UnitToggle options={WEIGHT_OPTIONS} value={weightUnit} onChange={onWeightUnitChange} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {crops.map((crop) => (
                <NumberInput
                  key={crop.id}
                  label={crop.label}
                  value={parseFloat(((inputs.seedRatesKgPerHa[crop.id] ?? 0) * wf / af).toFixed(4))}
                  onChange={(v) => updateSeedRate(crop.id, v)}
                  suffix={`${wLabel}/${aLabel}`}
                  min={0}
                  step={0.1}
                  compact
                  accentColor={crop.color}
                />
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="input-label mb-0">{t('demoFarm.seedCosts', { currency: currencyCode, weight: wLabel })}</label>
              <CurrencySelector
                currencyCode={currencyCode}
                onChange={onCurrencyChange}
                exchangeRate={exchangeRate}
                isLive={isLive}
                isLoading={isLoading}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {crops.map((crop) => (
                <NumberInput
                  key={crop.id}
                  label={crop.label}
                  value={parseFloat(((inputs.seedCostsLocalPerKg[crop.id] ?? 0) / wf).toFixed(4))}
                  onChange={(v) => updateSeedCost(crop.id, v)}
                  suffix={`${currencyCode}/${wLabel}`}
                  min={0}
                  step={100}
                  compact
                  accentColor={crop.color}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Results Card */}
      <div className="card !p-4 md:!p-5">
        <h4 className="font-semibold text-spi-charcoal mb-3 flex items-center gap-2 text-sm">
          <svg className="w-4 h-4 text-spi-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          {t('demoFarm.resultsTitle')}
        </h4>
        <div className="space-y-2">
          {crops.map((crop) => (
            <div
              key={crop.id}
              className="p-3 rounded-lg border-l-4"
              style={{
                borderLeftColor: crop.color,
                background: `linear-gradient(135deg, ${getCropBgColor(crop.color)} 0%, white 100%)`,
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-spi-charcoal text-sm">{crop.label}</span>
                <CalculationBreakdown
                  type="demoFarm"
                  cropLabel={crop.label}
                  inputs={inputs}
                  cropId={crop.id}
                  currencyCode={currencyCode}
                  areaUnit={areaUnit}
                  weightUnit={weightUnit}
                  result={{
                    seedKg: results.seedRequirementsKg[crop.id] ?? 0,
                    hectares: results.hectaresPerCrop[crop.id] ?? 0,
                    costLocal: results.costsLocal[crop.id] ?? 0,
                    costUsd: results.costsUsd[crop.id] ?? 0,
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                <div className="bg-white/60 rounded px-2 py-1">
                  <span className="text-spi-slate">{t('demoFarm.seed')}</span>{' '}
                  <span className="font-medium">{formatNumber((results.seedRequirementsKg[crop.id] ?? 0) * wf)} {wLabel}</span>
                </div>
                <div className="bg-white/60 rounded px-2 py-1">
                  <span className="text-spi-slate">{t('demoFarm.area')}</span>{' '}
                  <span className="font-medium">{formatNumber((results.hectaresPerCrop[crop.id] ?? 0) * af)} {aLabel}</span>
                </div>
                <div className="bg-white/60 rounded px-2 py-1">
                  <span className="text-spi-slate">{t('demoFarm.cost')}</span>{' '}
                  <span className="font-medium">{formatCurrency(results.costsLocal[crop.id] ?? 0, currencyCode)}</span>
                </div>
                <div className="bg-white/60 rounded px-2 py-1">
                  <span className="text-spi-slate">{t('demoFarm.usd')}</span>{' '}
                  <span className="font-medium">{formatCurrency(results.costsUsd[crop.id] ?? 0, 'USD')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 p-3 bg-gradient-to-r from-spi-green/10 to-spi-green/5 rounded-lg border border-spi-green/20">
          <div className="font-bold text-spi-green-dark mb-2 text-sm">{t('demoFarm.totalTitle')}</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-spi-slate">{t('demoFarm.totalSeed')}</span>{' '}
              <span className="font-semibold">{formatNumber(results.totalSeedKg * wf)} {wLabel}</span>
            </div>
            <div>
              <span className="text-spi-slate">{t('demoFarm.totalArea')}</span>{' '}
              <span className="font-semibold">{formatNumber(inputs.farmHectares * af)} {aLabel}</span>
            </div>
            <div>
              <span className="text-spi-slate">{t('demoFarm.totalCost')}</span>{' '}
              <span className="font-semibold">{formatCurrency(results.totalCostLocal, currencyCode)}</span>
            </div>
            <div>
              <span className="text-spi-slate">{t('demoFarm.usd')}</span>{' '}
              <span className="font-semibold">{formatCurrency(results.totalCostUsd, 'USD')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
